import { Stack, Tooltip, Typography } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useIsAdmin } from "src/hooks/useIsAdmin";
import UserAvatar from "./components/UserAvatar";
import { useMemo } from "react";
import {
  adminLinks,
  coachLinks,
  coordinatorLinks,
  developerLinks,
  // defaultLinks,
  leadProviderLinks,
  LinkType,
  operationsLinks,
  providerLinks,
} from "./links";
import HolaVidaLogoIcon from "src/assets/icons/HolaVidaLogoIcon";
import { theme } from "src/assets/theme";
import { useClinician } from "src/context/SessionProvider/ClinicianProvider";
import { Paths } from "src/constants/paths";

interface Props {
  item: LinkType;
}

const BASE_PATH = "/dashboard";

const NavigationLink = ({ item }: Props) => {
  const { Icon, title, link } = item;
  const { pathname } = useLocation();

  const isSelected = useMemo(() => {
    if (pathname === BASE_PATH && link === pathname) return true;

    const currentPath = pathname.replace(BASE_PATH, "");
    const currentLink = link.replace(BASE_PATH, "");

    if (currentLink === "") return false;
    return currentPath.includes(currentLink.split("?")[0]);
  }, [link, pathname]);

  return (
    <Tooltip title={<Typography>{title}</Typography>} placement="right">
      <Link
        to={link}
        style={{
          textDecoration: "none",
          color: isSelected ? theme.palette.secondary.main : "#15202b",
          display: "block",
        }}
      >
        <Icon sx={{ fontSize: isSelected ? "2.25rem" : "1.875rem" }} />
      </Link>
    </Tooltip>
  );
};

const SideNavBar = () => {
  const isAdmin = useIsAdmin();
  const leadProvider = useClinician((clinician) =>
    clinician.roles.some((role) => role.name === "Lead Provider"),
  );
  const provider = useClinician((clinician) =>
    clinician.roles.some((role) => role.name === "Provider"),
  );
  const coordinator = useClinician((clinician) =>
    clinician.roles.some((role) => role.name === "Coordinator"),
  );
  const coach = useClinician((clinician) =>
    clinician.roles.some((role) => role.name === "Coach"),
  );
  const operations = useClinician((clinician) =>
    clinician.roles.some((role) => role.name === "Operations"),
  );

  const developer = useClinician((clinician) =>
    clinician.roles.some((role) => role.name === "Developer"),
  );

  const navLinks = useMemo(() => {
    // populate array with links that correspond to the role
    const roleLinks: LinkType[] = [
      ...(leadProvider ? leadProviderLinks : []),
      ...(provider ? providerLinks : []),
      ...(coordinator ? coordinatorLinks : []),
      ...(coach ? coachLinks : []),
      ...(isAdmin ? adminLinks : []),
      ...(operations ? operationsLinks : []),
      ...(developer ? developerLinks : []),
    ];
    //combine role links with default link
    const links = roleLinks.filter((item) => item.active);

    const map = new Map<string, LinkType>();
    //iterate over each link and set it to the map, this will remove any duplicate
    links.forEach((item) => map.set(item.link, item));

    return Array.from(map.values()).map((link) => (
      <NavigationLink item={link} key={link.title} />
    ));
  }, [
    coach,
    coordinator,
    developer,
    isAdmin,
    leadProvider,
    operations,
    provider,
  ]);

  return (
    <Stack
      height={window.innerHeight - 8}
      gap="1.2rem"
      direction="column"
      alignItems="center"
      pr={0.5}
      pl={0.5}
      sx={{ borderRight: "1px solid #E5E5E5", height: "100%" }}
    >
      <Link to={Paths.DASHBOARD}>
        <HolaVidaLogoIcon />
      </Link>
      <Stack gap="1rem" alignItems="center">
        {navLinks}
      </Stack>
      <UserAvatar />
    </Stack>
  );
};

export default SideNavBar;
